/**
 * Ambient analytics scope (IC-132). `TabContent` provides the base scope
 * (report or tool); nested surfaces override parts of it with `AnalyticsScope`.
 * The emit hooks in `./hooks` read the merged value, so a call site passes only
 * its own props. A value with no `host` means "not in scope": hooks emit nothing.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { AnalyticsScopeProps, PanelItemTypeToken } from './events';

export type AnalyticsScopeValue = AnalyticsScopeProps;

export const AnalyticsScopeContext = createContext<AnalyticsScopeValue | null>(null);

export function useAnalyticsScope(): AnalyticsScopeValue | null {
  return useContext(AnalyticsScopeContext);
}

function definedOnly(value: Partial<AnalyticsScopeValue>): AnalyticsScopeValue {
  const out: Record<string, unknown> = {};
  for (const [key, v] of Object.entries(value)) {
    if (v !== undefined) out[key] = v;
  }
  return out as AnalyticsScopeValue;
}

/** Merges `value` over the parent scope. Undefined fields never erase parent fields. */
export function AnalyticsScope({
  value,
  children,
}: Readonly<{ value: Partial<AnalyticsScopeValue>; children: ReactNode }>) {
  const parent = useAnalyticsScope();
  const { host, report_key, tool, layout_type, surface, item_type } = value;
  // Depends on the primitives, never on the `value` object, so inline literals stay stable.
  const merged = useMemo(
    () => ({ ...parent, ...definedOnly({ host, report_key, tool, layout_type, surface, item_type }) }),
    [parent, host, report_key, tool, layout_type, surface, item_type],
  );
  return <AnalyticsScopeContext.Provider value={merged}>{children}</AnalyticsScopeContext.Provider>;
}

interface BaseScopeInput {
  type: string | undefined;
  reportKey?: string;
  layoutType?: string;
  componentKey?: string;
}

/** Base scope for a tab: a report tab is `host=report`, anything else is `host=tool`. */
export function baseScopeFor({ type, reportKey, layoutType, componentKey }: BaseScopeInput): AnalyticsScopeValue {
  if (type === 'report') {
    const layout = layoutType === 'composite' || layoutType === 'dashboard' ? layoutType : 'grid';
    return definedOnly({ host: 'report', report_key: reportKey, layout_type: layout });
  }
  return definedOnly({ host: 'tool', tool: componentKey });
}

/** Memoised on the four primitive inputs (never on the tab or toolConfig objects). */
export function useBaseAnalyticsScope(input: BaseScopeInput): AnalyticsScopeValue {
  const { type, reportKey, layoutType, componentKey } = input;
  return useMemo(
    () => baseScopeFor({ type, reportKey, layoutType, componentKey }),
    [type, reportKey, layoutType, componentKey],
  );
}

export function reportViewScope(viewType: 'grid' | 'dashboard'): AnalyticsScopeValue {
  return { layout_type: viewType };
}

export function compositeMemberScope(reportKey: string): AnalyticsScopeValue {
  return { report_key: reportKey, surface: 'composite_member' };
}

export function dashboardPanelScope(itemType: PanelItemTypeToken | undefined): AnalyticsScopeValue {
  return { surface: 'dashboard_panel', item_type: itemType };
}
